import { CdkDragDrop, moveItemInArray, transferArrayItem } from "@angular/cdk/drag-drop";
import { Component, EventEmitter, OnInit, Output } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";

import { AuthUser } from "@app/auth/model/auth-user";
import { ALL_VIEW_COLUMNS, ProjectViewColumn } from "@app/project/constants/all-view-columns.constants";
import { ProjectView } from "@app/project/model/project-view";
import { ProjectViewActions } from "@app/project/store/actions";
import { ProjectViewDispatchers } from "@app/project/store/dispatchers/project-view.dispatchers";
import { take } from "rxjs/operators";

@Component({
    selector: "app-edit-project-view",
    templateUrl: "./edit-project-view.component.html",
    styleUrls: ["./edit-project-view.component.scss"],
})
export class EditProjectViewComponent implements OnInit {
    projectView: ProjectView;
    entityId: number;
    userId: number;
    authUser: AuthUser;

    @Output() closeEditProjectView: EventEmitter<any> = new EventEmitter();
    callbackEmitter: EventEmitter<any> = new EventEmitter();
    editView: ProjectView;
    items: ProjectViewColumn[] = [];

    basket: ProjectViewColumn[] = [];
    projectViewForm: FormGroup = this.formBuilder.group({
        name: ["", Validators.required],
        visibility: [""],
    });

    constructor(private formBuilder: FormBuilder, private readonly projectViewDispatcher: ProjectViewDispatchers) {}

    ngOnInit(): void {
        this.editView = { ...this.projectView };

        // If the view that is being edited has an entityId, it is seen by everyone in the company,
        // so we want to set the radio button to display that. 1 is for entire company, 0 is for only me.
        this.projectViewForm.controls.visibility.setValue(this.editView?.entityId ? 1 : 0);

        // Sanity check
        const invalidKeys = this.editView?.viewData?.filter(key => !ALL_VIEW_COLUMNS[key]);
        if (invalidKeys?.length) {
            console.error(`Unrecognized project view keys:`, invalidKeys, this.editView);
        }

        // Partition available columns into those in the selected view (this.basket) and those that aren't (this.items)
        Object.values(ALL_VIEW_COLUMNS)
            .filter(col => this.hasPermission(col))
            .forEach(col => {
                const isInView = this.editView?.viewData?.includes(col.viewKey);
                if (isInView) {
                    this.basket.push(col);
                } else {
                    this.items.push(col);
                }
            });

        // Sort options alphabetically
        this.items.sort((a, b) => a.viewHeader.localeCompare(b.viewHeader));

        // Sort view columns by original API order
        const findIndex = (col: ProjectViewColumn) => this.editView?.viewData?.findIndex(k => k === col.viewKey);
        this.basket.sort((a, b) => findIndex(a) - findIndex(b));

        if (this.editView.name) {
            this.projectViewForm.controls.name.setValue(this.editView.name);
        }
    }

    private hasPermission(col: ProjectViewColumn): boolean {
        if (!col.permission) {
            return true;
        }

        if (this.authUser?.permissions?.includes(col.permission)) {
            return true;
        }

        return false;
    }

    drop(event: CdkDragDrop<string[]>) {
        if (event.previousContainer === event.container) {
            moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
        } else {
            transferArrayItem(event.previousContainer.data, event.container.data, event.previousIndex, event.currentIndex);
        }
        this.editView.viewData = this.basket.map(column => column.viewKey);
    }

    onDefaultChange(event) {
        this.editView.isDefault = event.checked;
    }

    saveView(isNew: boolean) {
        if (isNew) {
            // If we are saving a new view, we need to set id to null and userinfoId to current user id,
            // in case we are copying a view made by a different user
            this.editView.id = null;
            this.editView.userinfoId = this.userId;
            if (!this.projectViewForm.controls.name.touched) {
                // If the name hasn't been changed, append (Copy) to name when saving new
                this.projectViewForm.controls.name.setValue(this.projectViewForm.controls.name.value + " (Copy)");
            }
        }
        if (this.projectViewForm.controls.visibility.value === 0) {
            // if the user selects "only me" the projectview should now have an entityId so it doesn't
            // come back in the projects query for other users
            this.editView.entityId = null;
        } else {
            // Otherwise everyone should see it so set the entityId
            this.editView.entityId = this.entityId;
        }
        this.editView.name = this.projectViewForm.controls.name.value;
        this.callbackEmitter.pipe(take(1)).subscribe(action => {
            if (action?.type === ProjectViewActions.updateSuccess.type || action?.type === ProjectViewActions.createSuccess.type) {
                this.projectViewDispatcher.dispatchUpdateProjectViewSuccessAction(action.projectView);
            }
            // Once we get a response back from creating/updating a project view,
            // dispatch an action to update the state
            this.closeEditProjectView.emit();
        });
        this.projectViewDispatcher.upsertProjectView(this.editView, this.callbackEmitter);
    }

    get canSave() {
        return this.editView.userinfoId === this.userId && this.projectView.id;
    }

    cancel() {
        this.closeEditProjectView.emit();
    }

    delete() {
        this.callbackEmitter.subscribe(action => {
            this.projectViewDispatcher.dispatch(action);
            this.closeEditProjectView.emit();
        });
        this.projectViewDispatcher.deleteProjectView(this.editView, this.callbackEmitter, this.authUser);
    }
}
